import { Paper } from '@mui/material';
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { Booking, BookingState } from '@thro-types/leihbar';

import { isAfter } from 'date-fns';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { useBookingMutation } from 'api/hooks/leihbar';
import { ConfirmationDialog, DataGrid, TypedGridColumns } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { formatToDateAndTime } from 'utils/dates';

import { TabPanels } from '../Daily/OrderDetail';
import { BookingEditModal } from '../Products/ProductBookingHistory/components/BookingEditModal';
import { BookingRowWithTooltipEndTimeOverdue } from './BookingRowWithTooltipEndTimeOverdue';
import { BookingGridRowActions } from './rowActionsBookingDataGrid';

interface Props {
  bookings: Booking[];
  selected: GridRowSelectionModel;
  handleSelect: (selected: GridRowSelectionModel) => void;
  tab: TabPanels;
}

export const BookingDatagrid = ({ bookings, selected, handleSelect, tab }: Props) => {
  const { t } = useTranslation();
  const [bookingRef, setBookingRef] = useState<Booking | undefined>(undefined);
  const { id: orderId } = useParams();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const { cancel } = useBookingMutation();

  const handleEdit = useCallback((booking: Booking) => {
    setBookingRef(booking);
    setEditModalOpen(true);
  }, []);

  const handleStorno = useCallback((booking: Booking) => {
    setBookingRef(booking);
    setCancelModalOpen(true);
  }, []);

  const handleCancel = useCallback(
    async (booking: Booking) => {
      try {
        await cancel.mutateAsync({ id: booking.id });
        setBookingRef(undefined);
        setCancelModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ['orders', Number(orderId)] });
        handleSelect(selected.filter(id => id !== booking.id));
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.booking.alerts.successfulCancel()))
        );
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.booking.alerts.failedCancel())));
      }
    },
    [cancel, queryClient, orderId, dispatch, t, selected, handleSelect]
  );

  const isRowSelectable = ({ row, tab }: GridRowParams & { tab: TabPanels }) => {
    const isRowActive = row.deletedAt === null;
    const isTimeEligible = tab !== TabPanels.PICKUP || isAfter(new Date(row.endTime), new Date());

    switch (tab) {
      case TabPanels.PICKUP:
        return (
          isRowActive &&
          // isTimeEligible && // ? Maybe to be used again when annulation is implemented
          row.state !== BookingState.COLLECTED &&
          row.state !== BookingState.RETURNED
        );
      case TabPanels.RETURN:
        return isRowActive && row.state !== BookingState.RETURNED;
      default:
        return isRowActive && isTimeEligible;
    }
  };

  const columns: TypedGridColumns<
    Booking & { articleNote?: string; hint?: string; productNote?: string }
  > = [
    {
      field: 'articleId',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.articleNumber()),
      flex: 0.8,
      renderCell: params => params.row.article?.sku || '--'
    },
    {
      field: 'article',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.articleName()),
      flex: 1,
      renderCell: params => params.row.article?.product?.name || '--'
    },
    {
      field: 'productNote',
      headerName: t(translations.pages.leihbar.internalNote()),
      flex: 1,
      renderCell: params => params.row.article?.product?.internalNote || '---'
    },
    {
      field: 'hint',
      headerName: t(translations.pages.leihbar.product.create.hint()),
      flex: 1,
      renderCell: params => params.row.article?.product?.hint || '---'
    },
    {
      field: 'articleNote',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.internalNote()),
      flex: 1,
      renderCell: params => params.row.article?.internalNote || '---'
    },
    {
      field: 'startTime',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.plannedPickup()),
      flex: 0.8,
      renderCell: params => formatToDateAndTime(params.row.startTime),
      hideable: false
    },
    {
      field: 'collectedAt',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.collectedAt()),
      flex: 0.8,
      renderCell: params =>
        params.row.collectedAt ? formatToDateAndTime(params.row.collectedAt) : '---',
      hideable: false
    },
    {
      field: 'endTime',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.plannedReturn()),
      flex: 0.8,
      renderCell: params => formatToDateAndTime(params.row.endTime),
      hideable: false
    },
    {
      field: 'returnedAt',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.returnedAt()),
      flex: 0.8,
      renderCell: params =>
        params.row.returnedAt ? formatToDateAndTime(params.row.returnedAt) : '---',
      hideable: false
    },
    {
      field: 'comment',
      headerName: t(translations.pages.leihbar.orders.pickup.columns.commentByBorrower()),
      flex: 1,
      renderCell: params => params.row.comment || '---'
    },
    {
      field: 'actions',
      headerName: t(translations.common.actions()),
      minWidth: 100,
      type: 'actions',
      getActions: BookingGridRowActions({ handleEdit, handleStorno })
    }
  ];
  return (
    <Paper sx={{ height: '65vh' }}>
      <BookingEditModal
        closeHandler={() => {
          setBookingRef(undefined);
          setEditModalOpen(false);
        }}
        booking={bookingRef}
        open={editModalOpen}
      />
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.pages.leihbar.booking.dialogs.deleteConfirm.title())}
        onClose={() => {
          setBookingRef(undefined);
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          if (!bookingRef) return;
          handleCancel(bookingRef);
        }}
        isPending={cancel.isPending}
      >
        <div>{t(translations.pages.leihbar.booking.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <DataGrid
        columns={columns}
        rows={bookings || []}
        checkboxSelection
        rowSelectionModel={selected}
        onRowSelectionModelChange={handleSelect}
        isRowSelectable={params => isRowSelectable({ ...params, tab })}
        initialState={{
          columns: {
            columnVisibilityModel: {
              startTime: tab === TabPanels.PICKUP || tab === TabPanels.PICKING,
              collectedAt: tab === TabPanels.PICKUP,
              endTime: tab === TabPanels.RETURN,
              returnedAt: tab === TabPanels.RETURN
            }
          }
        }}
        getRowClassName={params =>
          params.row.deletedAt !== null
            ? 'row-canceled'
            : (params.row.collectedAt && tab === TabPanels.PICKUP) ||
              (params.row.returnedAt && tab === TabPanels.RETURN)
            ? 'row-collected'
            : ''
        }
        slots={{ row: BookingRowWithTooltipEndTimeOverdue }}
      />
    </Paper>
  );
};
